import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { find, get, isArray, compact, keys, pickBy, difference } from 'lodash';
import { SearchResultsMUI } from 'components/SearchResultsMUI/SearchResultsMUI';
import { GridRowSelectionModel, GridRowParams } from '@mui/x-data-grid-pro';
import { IStudyResultForGrid } from 'modules/Search/_types';
import { useActions } from 'utils/hooks/useActions';
import {
  ARCHIVE,
  BASKET,
  COPY,
  DETAIL,
  DOSE_MONITORING,
  DOSE_MONITORING_EXPORT,
  EXPORT,
  MOVE,
  MOVE_TO_FOLDER,
  MOVE_TO_TRASH,
  PROPOSAL_SHREDDING,
  SEARCH_FUNCTION_CHANGE_STATUSES,
  SEARCH_FUNCTION_EDIT_PATIENT,
  SEARCH_FUNCTION_EDIT_STUDY,
  SEARCH_FUNCTION_REORDER,
  SEARCH_FUNCTION_SPLIT,
  SEND,
  TAB_OPERATIONS,
  VIEWER,
  ECG,
  SEARCH_FUNCTION_ADD_TAGS,
  SEARCH_FUNCTION_SHARE_STUDY,
  PATIENT_STUDIES_GRID_SETTINGS_KEY,
  SEARCH_FUNCTION_SEARCH,
  LIST_PATIENT,
} from 'constants/constants';
import { decodeIID, generateIID } from 'utils/study';
import { ChangeStatusesDialog } from 'modules/Studies/StudyDetail/ChangeStatusesDialog';
import { ShareStudyDialog } from 'modules/Studies/StudyDetail/ShareStudyDialog';
import { StudiesActionDialog } from 'modules/Search/StudiesActionDialog';
import { useStudy } from 'utils/hooks/useStudy';
import useAlerts from 'components/Alerts/useAlerts';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import StudyDetailTagForm from 'modules/Studies/StudyDetail/StudyDetailTagForm';
import OverflowedDialog from 'components/Dialog/OverflowedDialog';
import { useGridExtendedInfo } from 'components/SearchResultsMUI/useGridExtendedInfo';
import { getStudies } from 'modules/Search/_api';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import { useNavigate } from 'react-router';
import { encryptId } from 'utils/hooks/useApp';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';

const actions = [
  DETAIL,
  ARCHIVE,
  BASKET,
  VIEWER,
  ECG,
  SEND,
  EXPORT,
  COPY,
  MOVE,
  MOVE_TO_FOLDER,
  PROPOSAL_SHREDDING,
  SEARCH_FUNCTION_REORDER,
  SEARCH_FUNCTION_SPLIT,
  SEARCH_FUNCTION_EDIT_STUDY,
  SEARCH_FUNCTION_EDIT_PATIENT,
  MOVE_TO_TRASH,
  SEARCH_FUNCTION_CHANGE_STATUSES,
  SEARCH_FUNCTION_ADD_TAGS,
  DOSE_MONITORING_EXPORT,
  DOSE_MONITORING,
  SEARCH_FUNCTION_SHARE_STUDY,
];

interface PatientStudiesGridProps {
  birthNumber: string;
  patientId?: string;
  updatedAt?: string;
}

const PatientStudiesGrid: React.FC<PatientStudiesGridProps> = ({
  birthNumber,
  patientId,
  updatedAt,
}) => {
  const { t } = useTranslation(['Studies', 'SearchResults', 'StudyDetail']);
  const { setBulkStudies } = useActions();
  const { getArchivesForFunctions } = useAppInfo();
  const { toggleLoader } = useAppGlobals();
  const navigate = useNavigate();
  const allStatuses = useSelector((state: any) => get(state, 'app.lists.statuses', []));
  const columnVisibilityModel = useSelector((state: any) =>
    get(state, `app.gridSettings.${PATIENT_STUDIES_GRID_SETTINGS_KEY}.columnVisibilityModel`, {}),
  );

  const [rows, setRows] = useState<IStudyResultForGrid[]>([]);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [items, setItems] = useState<any[]>([]);
  const [changeStatusesDialog, setChangeStatusesDialog] = useState<boolean>(false);
  const [RenderedChangeStatusesDialog, setRenderedChangeStatusesDialog] =
    useState<JSX.Element | null>(null);
  const [shareStudyDialog, setShareStudyDialog] = useState<boolean>(false);
  const [RenderedShareStudyDialog, setRenderedShareStudyDialog] = useState<JSX.Element | null>(
    null,
  );
  const [addTagsDialog, setAddTagsDialog] = useState<boolean>(false);
  const [RenderedAddTagsDialog, setRenderedAddTagsDialog] = useState<JSX.Element | null>(null);
  const [proposalShreddingDialog, setProposalShreddingDialog] = useState<boolean>(false);
  const [RenderedProposalShreddingDialog, setRenderedProposalShreddingDialog] =
    useState<JSX.Element | null>(null);
  const [studiesArchivationDialog, setStudiesArchivationDialog] = useState<boolean>(false);
  const [RenderedStudiesArchivationDialog, setRenderedStudiesArchivationDialog] =
    useState<JSX.Element | null>(null);
  const [studiesDeletionDialog, setStudiesDeletionDialog] = useState<boolean>(false);
  const [RenderedStudiesDeletionDialog, setRenderedStudiesDeletionDialog] =
    useState<JSX.Element | null>(null);

  const { deleteStudies, studyArchivation, studiesProposalsShredding } = useStudy();
  const { addErrorAlert, addSuccessAlert, addInfoAlert } = useAlerts();

  const { getExtendedInfoForColumn, loadExtendedInfo } = useGridExtendedInfo({
    rows,
    setRows,
    shouldStoreResults: false,
  });

  const fetchStudies = useCallback(async () => {
    if (!birthNumber || birthNumber === '-') return;

    toggleLoader(true);
    try {
      const archives = getArchivesForFunctions(SEARCH_FUNCTION_SEARCH);
      const data = await getStudies(
        {
          searchLevel: 'study',
          patient: { id: birthNumber },
          study: {
            accessionNumber: '',
            dateFrom: null,
            dateTo: null,
            description: '',
            referringPhysician: '',
            modalities: null,
          },
          source: {
            sources: archives.map((a: any) => a.id),
          },
        },
        {},
      );

      if (isArray(data)) {
        const processedRows: IStudyResultForGrid[] = data.map((study: any) => {
          const archiveCode = get(study, 'archive.code');
          const allStatusesForArchive = get(allStatuses, archiveCode || '', []);
          const iid = generateIID(study);
          const medoroStudyFlags = get(study, 'medoroStudyFlags', []);
          const statuses = compact(
            (medoroStudyFlags ? medoroStudyFlags : []).map((studyFlag: any) => {
              return find(allStatusesForArchive, { uuid: studyFlag }) || null;
            }),
          );
          return {
            ...study,
            iid,
            hashIID: encryptId(iid),
            statuses,
            patientName: {
              lastName: get(study, 'patient.name.lastName', ''),
              firstName: get(study, 'patient.name.firstName', ''),
            },
            patientBirthDate: get(study, 'patient.dateBirth', '') || '',
            patientSexTranslated: get(study, 'patient.sex', ''),
          };
        });
        setRows(processedRows);

        // Pre-load extended info for visible icon columns
        const hiddenColumns = keys(pickBy(columnVisibilityModel, (v: any) => v === false));
        const columnsForExtendedInfo = difference(
          ['comments', 'tags', 'sendInfos', 'stationAet', 'studyStatus'],
          hiddenColumns,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
        loadExtendedInfo(columnsForExtendedInfo, processedRows);
      }
    } catch (error) {
      addErrorAlert(t('Studies:loadError'));
    } finally {
      toggleLoader(false);
    }
  }, [birthNumber, allStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchStudies();
  }, [fetchStudies, updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // When selection changes, store selected studies to store
  useEffect(() => {
    setBulkStudies({
      type: LIST_PATIENT,
      items: isArray(selection) ? selection.map((iid) => find(rows, { iid })) : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const callFunctionByName = (nameFunction: string, item: string | null = null) => {
    const selectedItems = item ? [item] : selection;
    const studies = selectedItems.map((select) => {
      const findStudy = find(rows, { iid: select });
      const study = decodeIID(select as string);
      const studyID = get(study, 'studyID', '');
      const archiveID = get(study, 'archiveID', '');
      return {
        studyInstanceUid: studyID,
        archive: { id: archiveID, code: get(findStudy, 'archive.code', '') },
        medoroStudyFlags: findStudy ? get(findStudy, 'medoroStudyFlags', []) : [],
        accessionNumber: get(findStudy, 'accessionNumber', ''),
      };
    });
    setItems(studies);
    if (nameFunction === 'getChangeStatusesDialog') setChangeStatusesDialog(true);
    if (nameFunction === 'getAddTagsDialog') setAddTagsDialog(true);
    if (nameFunction === 'proposalShredding') setProposalShreddingDialog(true);
    if (nameFunction === 'archive') setStudiesArchivationDialog(true);
    if (nameFunction === 'moveToTrash') setStudiesDeletionDialog(true);
    if (nameFunction === 'getShareStudyDialog') setShareStudyDialog(true);
  };

  const handleLifeCycle = useCallback(async () => {
    fetchStudies();
  }, [fetchStudies]);

  const proposalShredding = useCallback(async () => {
    studiesProposalsShredding({
      studies: items,
      t,
      addInfoAlert,
      addSuccessAlert,
      addErrorAlert,
      callback: handleLifeCycle,
    });
  }, [
    items,
    t,
    addInfoAlert,
    addSuccessAlert,
    addErrorAlert,
    handleLifeCycle,
    studiesProposalsShredding,
  ]);

  const archive = useCallback(async () => {
    studyArchivation({
      studies: items,
      t,
      addSuccessAlert,
      addErrorAlert,
      callback: handleLifeCycle,
    } as any);
  }, [items, t, addSuccessAlert, addErrorAlert, handleLifeCycle, studyArchivation]);

  const moveToTrash = useCallback(async () => {
    deleteStudies({
      studies: items,
      t,
      addSuccessAlert,
      addErrorAlert,
      callback: handleLifeCycle,
    } as any);
  }, [items, t, addSuccessAlert, addErrorAlert, handleLifeCycle, deleteStudies]);

  const reloadTagsInGrid = useCallback(() => {
    getExtendedInfoForColumn({ tags: true }, { tags: false });
  }, [getExtendedInfoForColumn]);

  useEffect(() => {
    const RenderedChangeStatusesDialog =
      changeStatusesDialog && items ? (
        <ChangeStatusesDialog
          key={TAB_OPERATIONS}
          changeStatusesDialog={changeStatusesDialog}
          setChangeStatusesDialog={setChangeStatusesDialog}
          studies={items}
          callback={handleLifeCycle}
        />
      ) : null;
    setRenderedChangeStatusesDialog(RenderedChangeStatusesDialog);
  }, [items, changeStatusesDialog, handleLifeCycle]);

  useEffect(() => {
    const RenderedShareStudyDialog =
      shareStudyDialog && items ? (
        <ShareStudyDialog
          key={TAB_OPERATIONS}
          shareStudyDialog={shareStudyDialog}
          setShareStudyDialog={setShareStudyDialog}
          studies={items}
        />
      ) : null;
    setRenderedShareStudyDialog(RenderedShareStudyDialog);
  }, [items, shareStudyDialog]);

  useEffect(() => {
    const RenderedAddTagsDialog =
      addTagsDialog && items ? (
        <OverflowedDialog
          open={addTagsDialog}
          onClose={() => setAddTagsDialog(false)}
          aria-labelledby="form-dialog-title"
          maxWidth="md"
          fullWidth={true}
          title={t('StudyDetail:dialog.tag')}
        >
          <StudyDetailTagForm
            setDialogState={setAddTagsDialog}
            studies={items}
            reloadTagsInGrid={reloadTagsInGrid}
          />
        </OverflowedDialog>
      ) : null;
    setRenderedAddTagsDialog(RenderedAddTagsDialog);
  }, [items, addTagsDialog, t, reloadTagsInGrid]);

  useEffect(() => {
    const RenderedProposalShreddingDialog =
      proposalShreddingDialog && items ? (
        <StudiesActionDialog
          key={TAB_OPERATIONS}
          studiesActionDialog={proposalShreddingDialog}
          setStudiesActionDialog={setProposalShreddingDialog}
          action={proposalShredding}
          texts={{
            title: `${t('SearchResults:confirmProposalShreddingTitle')}`,
            dialogContentText: `${t('SearchResults:confirmProposalShredding', {
              par1: items.length,
            })}`,
          }}
        />
      ) : null;
    setRenderedProposalShreddingDialog(RenderedProposalShreddingDialog);
  }, [items, proposalShreddingDialog, proposalShredding, t]);

  useEffect(() => {
    const RenderedStudiesArchivationDialog =
      studiesArchivationDialog && items ? (
        <StudiesActionDialog
          key={TAB_OPERATIONS}
          studiesActionDialog={studiesArchivationDialog}
          setStudiesActionDialog={setStudiesArchivationDialog}
          action={archive}
          texts={{
            title: `${t('SearchResults:confirmArchivingStudiesTitle')}`,
            dialogContentText: `${t('SearchResults:confirmArchivingStudies', {
              par1: items.length,
            })}`,
          }}
        />
      ) : null;
    setRenderedStudiesArchivationDialog(RenderedStudiesArchivationDialog);
  }, [items, studiesArchivationDialog, archive, t]);

  useEffect(() => {
    const RenderedStudiesDeletionDialog =
      studiesDeletionDialog && items ? (
        <StudiesActionDialog
          key={TAB_OPERATIONS}
          studiesActionDialog={studiesDeletionDialog}
          setStudiesActionDialog={setStudiesDeletionDialog}
          action={moveToTrash}
          texts={{
            title: `${t('SearchResults:confirmDeletingStudiesTitle')}`,
            dialogContentText: `${t('SearchResults:confirmDeletingStudies', {
              par1: items.length,
            })}`,
          }}
        />
      ) : null;
    setRenderedStudiesDeletionDialog(RenderedStudiesDeletionDialog);
  }, [items, studiesDeletionDialog, moveToTrash, t]);

  const handleRowClick = (params: GridRowParams) => {
    navigate(
      `/study/${params.row.hashIID}?from=patientDetail${
        patientId ? `&patientId=${patientId}` : ''
      }`,
    );
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SearchResultsMUI
        gridKey={PATIENT_STUDIES_GRID_SETTINGS_KEY}
        rows={rows}
        setRows={setRows}
        actions={actions}
        selection={selection}
        setSelection={setSelection}
        onRowClick={handleRowClick}
        operations={{
          callFunctionByName,
        }}
        hideTopPanel={false}
        forcedAutoHeight={true}
        shouldStoreResults={false}
        from="patientDetail"
        patientId={patientId}
      />
      {RenderedChangeStatusesDialog}
      {RenderedShareStudyDialog}
      {RenderedAddTagsDialog}
      {RenderedProposalShreddingDialog}
      {RenderedStudiesArchivationDialog}
      {RenderedStudiesDeletionDialog}
    </Box>
  );
};

export default PatientStudiesGrid;
